import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export interface AppConfig {
  ENVIRONMENT_NAME: string;
  APP_NAME: string;
  APP_VERSION: string;
  BASE_URL?: string;
  KEYCLOAK_URL?: string;
  KEYCLOAK_REALM?: string;
  KEYCLOAK_CLIENTID?: string;
  KEYCLOAK_DISABLE_IFRAME?: boolean;
  LOG_LEVEL: string;
  APOLLO_API_URL: string;
  TIMEOUT_DELAY?: number;
  HTTP_TIMEOUT_MS?: number;
  googleMapKey?: string;
  FACILITY_SEARCH_RADIUS_KM?: number;
  CASE_MANAGER_URL?: string;
  ALBERTA_SUPPORTS_URL?: string;
  ALBERTA_ACCOMMODATION_CHARGES_URL?: string;
  MICROSOFT_CLARITY_PROJECT_ID?: string;
  DISABLE_AUTH_GUARD?: boolean;
  FEATURE_PREVIEW?: string;
  RESEARCH_INVITATION_URL?: string;
  RESEARCH_INVITATION_ENABLED?: boolean;
  RESEARCH_INVITATION_TIMING_MINUTES?: number;
}

type ConfigValueValidator = (value: unknown, key: keyof AppConfig) => void;

type RequiredValidationRule = {
  key: keyof AppConfig;
  validate: ConfigValueValidator;
};

type OptionalValidationRule = RequiredValidationRule;

export type SecuredAppConfig = AppConfig & {
  KEYCLOAK_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_CLIENTID: string;
  KEYCLOAK_DISABLE_IFRAME: boolean;
  TIMEOUT_DELAY: number;
};

function assertNonEmptyString(value: unknown, key: keyof AppConfig): void {
  if (typeof value === 'string' && value.trim().length > 0) {
    return;
  }

  throw new Error(`Missing or invalid config value for ${key}.`);
}

function assertFiniteNumber(value: unknown, key: keyof AppConfig): void {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return;
  }

  throw new Error(`Missing or invalid config value for ${key}.`);
}

function assertBoolean(value: unknown, key: keyof AppConfig): void {
  if (typeof value === 'boolean') {
    return;
  }

  throw new Error(`Missing or invalid config value for ${key}.`);
}

function assertAbsoluteUrlString(value: unknown, key: keyof AppConfig): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`Missing or invalid config value for ${key}.`);
  }

  const trimmed = value.trim();

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return;
    }
  } catch {
    // fall through to throw the standard validation error below.
  }

  throw new Error(`Missing or invalid config value for ${key}.`);
}

function assertPositiveNumber(value: unknown, key: keyof AppConfig): void {
  assertFiniteNumber(value, key);
  if ((value as number) <= 0) {
    throw new Error(`Missing or invalid config value for ${key}.`);
  }
}

function assertNonNegativeNumber(value: unknown, key: keyof AppConfig): void {
  assertFiniteNumber(value, key);
  if ((value as number) < 0) {
    throw new Error(`Missing or invalid config value for ${key}.`);
  }
}

const validatePresence: RequiredValidationRule[] = [
  { key: 'APOLLO_API_URL', validate: assertAbsoluteUrlString },
  { key: 'ENVIRONMENT_NAME', validate: assertNonEmptyString },
  { key: 'APP_NAME', validate: assertNonEmptyString },
  { key: 'APP_VERSION', validate: assertNonEmptyString },
  { key: 'LOG_LEVEL', validate: assertNonEmptyString },
  { key: 'TIMEOUT_DELAY', validate: assertPositiveNumber },
];

const validateIfPresent: OptionalValidationRule[] = [
  { key: 'HTTP_TIMEOUT_MS', validate: assertPositiveNumber },
  { key: 'DISABLE_AUTH_GUARD', validate: assertBoolean },
  { key: 'RESEARCH_INVITATION_ENABLED', validate: assertBoolean },
  {
    key: 'RESEARCH_INVITATION_TIMING_MINUTES',
    validate: assertNonNegativeNumber,
  },
  { key: 'RESEARCH_INVITATION_URL', validate: assertAbsoluteUrlString },
  { key: 'googleMapKey', validate: assertNonEmptyString },
];

function isNavigatorConfig(config: AppConfig): boolean {
  const marker = `${config.APP_NAME} ${config.BASE_URL ?? ''}`
    .trim()
    .toLowerCase();
  return marker.includes('navigator');
}

function isSecuredAppConfig(config: AppConfig): config is SecuredAppConfig {
  try {
    assertAbsoluteUrlString(config.KEYCLOAK_URL, 'KEYCLOAK_URL');
    assertNonEmptyString(config.KEYCLOAK_REALM, 'KEYCLOAK_REALM');
    assertNonEmptyString(config.KEYCLOAK_CLIENTID, 'KEYCLOAK_CLIENTID');
    assertBoolean(config.KEYCLOAK_DISABLE_IFRAME, 'KEYCLOAK_DISABLE_IFRAME');
    assertPositiveNumber(config.TIMEOUT_DELAY, 'TIMEOUT_DELAY');
    return true;
  } catch {
    return false;
  }
}

function shouldSkipRequiredValidation(
  config: AppConfig,
  key: keyof AppConfig
): boolean {
  // Navigator intentionally omits TIMEOUT_DELAY for now.
  // Remove this bypass when Navigator adopts TIMEOUT_DELAY.
  return key === 'TIMEOUT_DELAY' && isNavigatorConfig(config);
}

function normalizeConfig(config: AppConfig): AppConfig {
  const normalized: AppConfig = { ...config };
  const writable = normalized as Record<keyof AppConfig, unknown>;

  for (const key of Object.keys(normalized) as Array<keyof AppConfig>) {
    const value = normalized[key];
    if (typeof value === 'string') {
      writable[key] = value.trim();
    }
  }

  return normalized;
}

function validateConfig(config: AppConfig): AppConfig {
  const normalizedConfig = normalizeConfig(config);

  for (const rule of validatePresence) {
    const value = normalizedConfig[rule.key];
    if (
      shouldSkipRequiredValidation(normalizedConfig, rule.key) &&
      (value === undefined || value === null)
    ) {
      continue;
    }
    rule.validate(value, rule.key);
  }

  if (!isNavigatorConfig(normalizedConfig)) {
    assertAbsoluteUrlString(normalizedConfig.KEYCLOAK_URL, 'KEYCLOAK_URL');
    assertNonEmptyString(normalizedConfig.KEYCLOAK_REALM, 'KEYCLOAK_REALM');
    assertNonEmptyString(
      normalizedConfig.KEYCLOAK_CLIENTID,
      'KEYCLOAK_CLIENTID'
    );
    assertBoolean(
      normalizedConfig.KEYCLOAK_DISABLE_IFRAME,
      'KEYCLOAK_DISABLE_IFRAME'
    );
  }

  for (const rule of validateIfPresent) {
    const value = normalizedConfig[rule.key];
    if (value !== undefined && value !== null) {
      rule.validate(value, rule.key);
    }
  }

  return normalizedConfig;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigInitService {
  private readonly http = inject(HttpClient);
  private readonly configSignal = signal<AppConfig | null>(null);

  readonly isLoaded = computed(() => this.configSignal() !== null);
  readonly isDevOrTest = computed(() => {
    const env = (this.configSignal()?.ENVIRONMENT_NAME ?? '').toLowerCase();
    return env.endsWith('dev') || env.endsWith('test');
  });
  readonly isLocalDevOrTest = computed(() => {
    const env = (this.configSignal()?.ENVIRONMENT_NAME ?? '').toLowerCase();
    return env.includes('local') || env.includes('dev') || env.includes('test');
  });

  async loadConfig(): Promise<AppConfig> {
    const existingConfig = this.configSignal();
    if (existingConfig) {
      return existingConfig;
    }

    const config = await firstValueFrom(
      this.http.get<AppConfig>('/assets/config/runtime/config.json')
    );

    const validatedConfig = validateConfig(config);
    this.configSignal.set(validatedConfig);
    return validatedConfig;
  }

  getConfig(): AppConfig {
    const cfg = this.configSignal();
    if (!cfg) {
      throw new Error('Config not loaded. Call loadConfig() first.');
    }
    return cfg;
  }

  getSecuredConfig(): SecuredAppConfig {
    const cfg = this.getConfig();
    if (!isSecuredAppConfig(cfg)) {
      throw new Error(
        'Secure config values missing/invalid (KEYCLOAK_*, TIMEOUT_DELAY). Use getConfig() for Navigator; secured apps must define these values.'
      );
    }

    return cfg;
  }

  getValue<T = unknown>(key: keyof AppConfig): T | undefined {
    return this.getConfig()[key] as T;
  }

  updateConfig(updates: Partial<AppConfig>): void {
    const current = this.configSignal();
    if (current) {
      this.configSignal.set({ ...current, ...updates });
    }
  }
}
